import { useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Clock, Calendar, FolderKanban, ShieldAlert, Check } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const packages = [
  {
    icon: Clock,
    title: 'Advisory Retainer',
    description: 'Strategic guidance on-demand',
    hours: '8–16 hrs/month',
    price: 'From $2,500/mo',
    features: [
      'Monthly strategy sessions',
      'Slack/email access',
      '24h response SLA',
      'Quarterly reviews',
    ],
  },
  {
    icon: Calendar,
    title: 'Fractional CXO',
    description: 'Part-time executive leadership',
    hours: '1–2 days/week',
    price: 'From $6,000/mo',
    features: [
      'Dedicated weekly time',
      'Team leadership',
      'Stakeholder management',
      'OKR tracking',
    ],
    highlighted: true,
  },
  {
    icon: FolderKanban,
    title: 'Project-Based',
    description: 'Fixed scope deliverables',
    hours: 'Fixed scope + milestones',
    price: 'From $8,000',
    features: [
      'Defined deliverables',
      'Milestone payments',
      'Project timeline',
      'Outcome-based',
    ],
  },
  {
    icon: ShieldAlert,
    title: 'Emergency Response',
    description: 'CISO hotline + incident support',
    hours: 'On-call availability',
    price: 'From $500/hr',
    features: [
      '2-hour response time',
      'Incident response',
      'Security audits',
      'Compliance review',
    ],
  },
];

export default function PackagesSection() {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Title animation
      gsap.fromTo(
        titleRef.current,
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          scrollTrigger: {
            trigger: titleRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Cards stagger animation
      cardsRef.current.forEach((card, index) => {
        if (card) {
          gsap.fromTo(
            card,
            { y: 50, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.6,
              delay: index * 0.1,
              scrollTrigger: {
                trigger: card,
                start: 'top 85%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        }
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-flowing bg-navy relative z-40"
    >
      <div className="w-full px-6 lg:px-12 max-w-7xl mx-auto">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-16 opacity-0">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-teal mb-4 block">
            Engagement Models
          </span>
          <h2 className="font-heading text-h2 sm:text-h1 font-bold text-white max-w-3xl mx-auto">
            Engagement packages that fit your pace
          </h2>
        </div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {packages.map((pkg, index) => (
            <div
              key={pkg.title}
              ref={(el) => { cardsRef.current[index] = el; }}
              className={`relative opacity-0 group ${
                pkg.highlighted ? 'lg:-mt-4 lg:mb-4' : ''
              }`}
            >
              <div
                className={`h-full surface-panel p-6 transition-all duration-300 ${
                  pkg.highlighted
                    ? 'border-teal/50 shadow-glow'
                    : 'hover:border-teal/30'
                }`}
              >
                {/* Highlighted Badge */}
                {pkg.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 text-xs font-medium bg-teal text-navy rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                {/* Icon */}
                <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center mb-4">
                  <pkg.icon className="w-6 h-6 text-teal" />
                </div>

                {/* Content */}
                <h3 className="font-heading text-lg font-semibold text-white mb-1">
                  {pkg.title}
                </h3>
                <p className="text-sm text-slate mb-4">{pkg.description}</p>

                {/* Hours */}
                <p className="text-xs font-mono text-teal mb-4">{pkg.hours}</p>

                {/* Price */}
                <p className="text-2xl font-heading font-bold text-white mb-6">
                  {pkg.price}
                </p>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-teal mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <button
                  onClick={() => navigate('/register')}
                  className={`w-full py-3 rounded-xl font-semibold text-sm transition-colors ${
                    pkg.highlighted
                      ? 'bg-teal text-navy hover:bg-teal-dark'
                      : 'bg-white/5 text-white hover:bg-white/10'
                  }`}
                >
                  Learn more
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
